import React, {useState, useEffect} from 'react';
import {Pressable} from 'react-native';
import {FieldError, UseControllerProps, useController} from 'react-hook-form';
import {
  Box,
  FormControl,
  HStack,
  Popover,
  Radio,
  Stack,
  Text,
} from 'native-base';
import IDropDownOption from '../../../interfaces/ui/dropdown-option.interface';
import {message} from '../../../utils/messages';

interface SimpleRadioProps extends UseControllerProps<any> {
  label: string;
  error: FieldError | undefined;
  disabled?: boolean;
  options: IDropDownOption[];
  labelIcon?: JSX.Element;
  labelIconHelpText?: JSX.Element;
  defaultValue?: string;
  isRequired?: boolean;
  onChange?: (value: any) => void;
}

const SimpleRadio = (props: SimpleRadioProps) => {
  const {
    control,
    name,
    rules,
    label,
    options,
    onChange,
    error,
    defaultValue,
    labelIcon,
    labelIconHelpText,
    isRequired,
  } = props;
  const [value, setValue] = useState(
    defaultValue ? defaultValue : options[0].value,
  );
  const {field} = useController({
    control,
    defaultValue: defaultValue ?? '',
    name,
    rules,
  });

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
    }
  }, [defaultValue]);

  const getMessage = (text: string) => {
    return message(text).trim();
  };

  const handleOnValueChange = (nextValue: string) => {
    field.onChange(nextValue);
    if (onChange) {
      onChange(nextValue);
    }
  };

  const LabelIcon = () => {
    return labelIcon ?? <></>;
  };

  const hasHelpSection = () => {
    return labelIconHelpText && labelIcon;
  };

  const HelpSection = () => {
    return (
      <HStack
        alignContent={'center'}
        justifyItems="center"
        justifyContent={'center'}>
        <FormControl.Label>
          <Text color="#858597">{getMessage(label)}</Text>
        </FormControl.Label>
        <HStack
          flex={1}
          alignItems="center"
          paddingLeft={2}
          justifyContent="flex-start">
          <Popover
            placement="bottom"
            trigger={triggerProps => {
              return (
                <Pressable {...triggerProps}>
                  <LabelIcon />
                </Pressable>
              );
            }}
            offset={5}>
            <Popover.Content w="96%" rounded={10} marginLeft={3} marginTop={50}>
              <Popover.CloseButton
                marginTop={-3}
                marginRight={-2}
                _icon={{
                  size: 2.5,
                  color: '#FFFFFF',
                }}
              />
              <Popover.Body
                paddingTop={2}
                backgroundColor={'#0088FF'}
                paddingRight={18}
                height={86}>
                <Text
                  fontSize={14}
                  color="#FFFFFF"
                  fontWeight={'thin'}
                  allowFontScaling={true}>
                  {labelIconHelpText}
                </Text>
              </Popover.Body>
            </Popover.Content>
          </Popover>
        </HStack>
      </HStack>
    );
  };

  return (
    <Box width="100%" marginBottom={5}>
      <FormControl isRequired={isRequired} isInvalid={error != null}>
        {hasHelpSection() && <HelpSection />}
        {!hasHelpSection() && (
          <FormControl.Label>
            <Text color="#858597">{getMessage(label)}</Text>
          </FormControl.Label>
        )}

        <Radio.Group
          name="igtfRadioGroup"
          value={field.value}
          onChange={handleOnValueChange}
          defaultValue={defaultValue}>
          <Stack
            width={'100%'}
            direction={{
              base: 'row',
            }}
            justifyContent={'flex-start'}
            space={5}>
            {options.map(option => (
              <Radio
                _checked={{borderColor: '#0088FF'}}
                _icon={{
                  color: '#0088FF',
                }}
                key={`key-${option.value}`}
                value={option.value}
                my={1}
                paddingX={10}>
                {option.label}
              </Radio>
            ))}
          </Stack>
        </Radio.Group>
      </FormControl>
    </Box>
  );
};

export default SimpleRadio;
